import { z } from 'zod';
import {
  CreateProcessWebhook,
  CreateWebhook,
  UpdateWebhook,
  Webhook,
  ListWebhookLogs,
  ListWebhookLogsByProcessRun,
} from '@robocorp/robocloud-webhooks-interface';
import { integrationWebhooksApi } from '~/utils/api';
import { WithWorkspaceId } from '~/types/common';

export const listWebhooks = async ({ workspaceId }: WithWorkspaceId) =>
  integrationWebhooksApi.get<Webhook[]>(`/workspaces/${workspaceId}/webhooks`);

export const listProcessWebhooks = async ({
  workspaceId,
  processId,
}: { processId: string } & WithWorkspaceId) =>
  integrationWebhooksApi.get<Webhook[]>(
    `/workspaces/${workspaceId}/processes/${processId}/webhooks`,
  );

export const getWebhook = async ({
  workspaceId,
  webhookId,
}: { webhookId: string } & WithWorkspaceId) =>
  integrationWebhooksApi.get<Webhook>(`/workspaces/${workspaceId}/webhooks/${webhookId}`);

export type EditWebhookInput = z.infer<(typeof UpdateWebhook)['http']['request']['body']>;
export const editWebhook = async ({
  workspaceId,
  webhookId,
  params,
}: { webhookId: string; params: EditWebhookInput } & WithWorkspaceId) =>
  integrationWebhooksApi.put<Webhook>(`/workspaces/${workspaceId}/webhooks/${webhookId}`, params);

export type CreateWebhookInput = z.infer<(typeof CreateWebhook)['http']['request']['body']>;
export const createWebhook = async ({
  workspaceId,
  params,
}: { params: CreateWebhookInput } & WithWorkspaceId) =>
  integrationWebhooksApi.post<Webhook>(`/workspaces/${workspaceId}/webhooks`, params);

export type CreateProcessWebhookInput = z.infer<
  (typeof CreateProcessWebhook)['http']['request']['body']
>;
export const createProcessWebhook = async ({
  workspaceId,
  processId,
  params,
}: { processId: string; params: CreateProcessWebhookInput } & WithWorkspaceId) =>
  integrationWebhooksApi.post<Webhook>(
    `/workspaces/${workspaceId}/processes/${processId}/webhooks`,
    params,
  );

export const deleteWebhook = async ({
  workspaceId,
  webhookId,
}: { webhookId: string } & WithWorkspaceId) =>
  integrationWebhooksApi.delete<Webhook>(`/workspaces/${workspaceId}/webhooks/${webhookId}`);

export const sendTestEvent = async ({
  workspaceId,
  webhookId,
}: { webhookId: string } & WithWorkspaceId) =>
  integrationWebhooksApi.post<void>(
    `/workspaces/${workspaceId}/webhooks/${webhookId}/send-test-event`,
  );

export type ListWebhookLogsResponse = z.infer<(typeof ListWebhookLogs)['http']['response']['200']>;
export type ListWebhookLogsQueryParams = z.infer<
  (typeof ListWebhookLogs)['http']['request']['queryString']
>;
export const listWebhookLogs = async ({
  workspaceId,
  webhookId,
  params,
}: { webhookId: string; params: ListWebhookLogsQueryParams } & WithWorkspaceId) => {
  const { size, cursor, showOnlyErrors, eventType } = params ?? {};
  return integrationWebhooksApi.get<ListWebhookLogsResponse>(
    `/workspaces/${workspaceId}/webhooks/${webhookId}/logs`,
    {
      size,
      cursor,
      showOnlyErrors,
      eventType,
    },
  );
};

export type ListWebhookLogsByProcessRunResponse = z.infer<
  (typeof ListWebhookLogsByProcessRun)['http']['response']['200']
>;
export type ListWebhookLogsByProcessRunPathParams = Omit<
  z.infer<(typeof ListWebhookLogsByProcessRun)['http']['request']['pathParams']>,
  'workspaceId'
>;
export type ListWebhookLogsByProcessRunParams = z.infer<
  (typeof ListWebhookLogsByProcessRun)['http']['request']['queryString']
>;
export const listWebhookLogsByProcessRun = async ({
  workspaceId,
  pathParams,
  params,
}: {
  pathParams: ListWebhookLogsByProcessRunPathParams;
  params: ListWebhookLogsByProcessRunParams;
} & WithWorkspaceId) => {
  const { processId, processRunId } = pathParams;
  const { size, cursor } = params ?? {};
  return integrationWebhooksApi.get<ListWebhookLogsByProcessRunResponse>(
    `/workspaces/${workspaceId}/processes/${processId}/process-runs/${processRunId}/logs`,
    {
      size,
      cursor,
    },
  );
};
